import { useEffect, useState } from "react";
import { Box, Dialog, IconButton, Link, Skeleton } from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import { useApi } from "../../hooks/useApi";
import { INK } from "./styles";

const IMAGE_NAME = /\.(png|jpe?g|gif|webp)$/i;

export default function InquiryAttachment({ attachment }: { attachment: { name: string; url: string } }) {
  const { getBlob } = useApi();
  const isImage = IMAGE_NAME.test(attachment.name);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let dropped = false;
    let objectUrl: string | null = null;
    setSrc(null);
    setFailed(false);
    getBlob(attachment.url)
      .then((blob) => {
        if (dropped) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err: { name?: string }) => {
        if (!dropped && err?.name !== 'AbortError') setFailed(true);
      });
    return () => {
      dropped = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [getBlob, attachment.url]);

  if (failed) {
    return <Box sx={{ fontSize: 12, color: INK.muted, mt: '6px' }}>{attachment.name} · could not load</Box>;
  }
  if (!src) {
    return isImage
      ? <Skeleton variant="rounded" width={240} height={140} sx={{ mt: '6px' }} />
      : <Skeleton variant="text" width={160} sx={{ mt: '4px' }} />;
  }
  if (isImage) {
    return (
      <>
        <Box component="button" type="button" aria-label={`Open ${attachment.name}`} onClick={() => setOpen(true)} sx={{ display: 'block', mt: '6px', p: 0, border: 0, bgcolor: 'transparent', cursor: 'zoom-in' }}>
          <Box component="img" src={src} alt={attachment.name} sx={{ display: 'block', maxWidth: 240, maxHeight: 240, borderRadius: '6px' }} />
        </Box>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth={false}
          slotProps={{ backdrop: { sx: { bgcolor: 'rgba(16,24,40,.85)' } }, paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', m: 0, overflow: 'visible' } } }}
        >
          <Box component="img" src={src} alt={attachment.name} sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
          <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ position: 'fixed', top: 12, right: 12, color: '#fff' }}>
            <CloseOutlined />
          </IconButton>
          <Link href={src} download={attachment.name} sx={{ position: 'fixed', bottom: 12, right: 16, fontSize: 12, color: '#fff' }}>Download</Link>
        </Dialog>
      </>
    );
  }
  return (
    <Link href={src} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '6px', fontSize: 12 }}>
      <AttachFileOutlined sx={{ fontSize: 14 }} />
      {attachment.name}
    </Link>
  );
}
